import { z } from 'zod'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import type { AnalyticsReference } from '@/analytics/schemas/analytics-reference.schemas'
import type { RiaCitatorSection } from '@/analytics/enums/ria.enums'

/**
 * Content Viewer Session Types
 *
 * Documents describe what is rendered, contexts describe where it was opened,
 * and presentation carries viewer-only state such as highlights or default tabs.
 */

/**
 * Render Format
 *
 * Defines HOW a document should be rendered.
 */
export enum RenderFormat {
  HTML = 'html',
  PDF = 'pdf',
  MARKDOWN = 'markdown', // Render by base HTML
}
export const RenderFormatSchema = z.nativeEnum(RenderFormat)

export enum ViewerDocumentKind {
  AGENCY = 'AGENCY',
  USER_FILE = 'USER_FILE',
  CASELAW = 'CASELAW',
  LRR = 'LRR',
  MED_SUMMARY_CONVERTED_PDF = 'MED_SUMMARY_CONVERTED_PDF',
  DOCKET = 'DOCKET',
}

export enum ViewerOpenContextKind {
  CHAT_CITATION = 'CHAT_CITATION',
  MED_CHRON_REFERENCE = 'MED_CHRON_REFERENCE',
  MED_CHRON_SOURCE = 'MED_CHRON_SOURCE',
  MED_SUMMARY_REFERENCE = 'MED_SUMMARY_REFERENCE',
  MED_SUMMARY_SOURCE = 'MED_SUMMARY_SOURCE',
  TIMELINE_REFERENCE = 'TIMELINE_REFERENCE',
  TIMELINE_SOURCE = 'TIMELINE_SOURCE',
  DRIVE_FILE = 'DRIVE_FILE',
  SELECTED_FILE = 'SELECTED_FILE',
}

export enum ViewerDownloadTargetKind {
  AGENCY_ORIGINAL = 'AGENCY_ORIGINAL',
  USER_FILE_ORIGINAL = 'USER_FILE_ORIGINAL',
  RICH_REFERENCE_DOCX = 'RICH_REFERENCE_DOCX',
}

export type ViewerUserFileRenderAsset = {
  format: RenderFormat.PDF | RenderFormat.MARKDOWN
}

export type ViewerHtmlRenderAsset = {
  format: RenderFormat.HTML
  sourceKind: ViewerDocumentKind.CASELAW | ViewerDocumentKind.LRR
  storagePath: string
}

/** Agency artifacts are authorized by exactly one independent reference owner. */
export type AgencyReferenceOwner = { conversationId: string; tableId?: never } | { tableId: string; conversationId?: never }

export type ViewerDownloadTarget =
  | ({ kind: ViewerDownloadTargetKind.AGENCY_ORIGINAL; referenceId: string } & AgencyReferenceOwner)
  | {
      kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL
      userFileId: string
    }
  | {
      kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX
      sourceKind: ViewerDocumentKind.CASELAW | ViewerDocumentKind.LRR
      storagePath: string
      fileName: string
    }

export type UserFileViewerDocument = {
  kind: ViewerDocumentKind.USER_FILE
  userFileId: string
  title: string
  // Omit renderAsset when the session cannot know the file's render format up front
  // (e.g. matter-artifact citations, which carry only the file id): the renderer then
  // resolves PDF vs markdown from the viewable-url response it fetches anyway.
  renderAsset?: ViewerUserFileRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

export type CaselawViewerDocument = {
  kind: ViewerDocumentKind.CASELAW
  s3Dir?: string | null
  s3Path: string
  parentId: string
  title: string
  caseStatus?: string | null
  renderAsset: ViewerHtmlRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

export type LrrViewerDocument = {
  kind: ViewerDocumentKind.LRR
  s3Path: string
  title: string
  contentStatus?: string | null
  renderAsset: ViewerHtmlRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

// A billing-summary source file's converted PDF (AI-94): non-PDF sources are extracted
// against a stored conversion, and citation highlight geometry is relative to it. The
// renderer resolves a short-lived signed URL by (assetId, userFileId) at render time.
export type MedSummaryConvertedPdfViewerDocument = {
  kind: ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF
  assetId: string
  userFileId: string
  title: string
  downloadTarget?: ViewerDownloadTarget
}

// Each owner resolves its saved docket record without a source file or provider request.
export type DocketViewerDocument = {
  kind: ViewerDocumentKind.DOCKET
  title: string
  downloadTarget?: ViewerDownloadTarget
} & (
  | { conversationId: string; citationId: string; tableId?: never; referenceId?: never }
  | { tableId: string; referenceId: string; conversationId?: never; citationId?: never }
)

export type ViewerDocument =
  | ({ kind: ViewerDocumentKind.AGENCY; referenceId: string; title: string; downloadTarget?: ViewerDownloadTarget } & AgencyReferenceOwner)
  | UserFileViewerDocument
  | CaselawViewerDocument
  | LrrViewerDocument
  | MedSummaryConvertedPdfViewerDocument
  | DocketViewerDocument

export type ViewerOpenContext =
  | {
      kind: ViewerOpenContextKind.CHAT_CITATION
      referenceId: string
      citationId: string
      turnId: string
      conversationId: string
      analyticsReference?: AnalyticsReference
    }
  | {
      kind: ViewerOpenContextKind.MED_CHRON_REFERENCE
      referenceId: string
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.MED_CHRON_SOURCE
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE
      referenceId: string
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.TIMELINE_REFERENCE
      referenceId: string
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.TIMELINE_SOURCE
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.DRIVE_FILE
      matterId?: string
    }
  | {
      kind: ViewerOpenContextKind.SELECTED_FILE
      conversationId?: string
    }

export type ViewerPresentation = {
  pdfExcerpt?: string
  pdfHighlights?: PaxtonHighlightArea[]
  markdownHighlights?: string[]
  startingPage?: number
  defaultTab?: CaseLawReferenceTab
  /** When opening the citator tab, which accordion section starts open. */
  defaultOpenSection?: RiaCitatorSection
}

export type ContentViewerSession = {
  document: ViewerDocument
  context: ViewerOpenContext
  presentation?: ViewerPresentation
}

/**
 * File Content Renderer Configuration
 *
 * Simplified discriminated union for file rendering.
 * The format field determines which renderer to use and which options are available.
 *
 * Key simplifications:
 * - GCS bucket is computed at render time from format (not stored)
 * - Options are flattened (no wrapper objects)
 * - Optional fields have sensible defaults
 *
 * Type-safe rendering where:
 * - PDF files get PDF-specific options (highlight areas, starting page)
 * - Markdown files get Markdown-specific options (sentence highlights)
 */
export type FileContentRendererConfig =
  | {
      format: RenderFormat.PDF
      highlights?: PaxtonHighlightArea[]
      startingPage?: number
    }
  | {
      format: RenderFormat.MARKDOWN
      highlights?: string[] | null
    }
